/**
 * Plays the level-up stinger when the character's level actually goes up.
 *
 * ─── Why it watches the level instead of listening for an event ─────────────
 * The backend does emit `level-up` (`core/economy/economy.service.ts`), but
 * that is a server-internal event, not something the socket forwards to this
 * client. What the client does get is `character:updated`, which
 * `connectGameStateSync` folds into `gameStateStore` — and CharacterService
 * pushes it on create/allocate/setColor/**level-up**. So the level on the
 * sheet is the authoritative signal, already arriving, with no new plumbing.
 *
 * Watching a value rather than an event means the comparison has to be
 * right, which is the whole of the logic below:
 *
 *  - The FIRST sheet is never a level-up. Going from `null` to level 5 is the
 *    character loading, and a stinger on every page load is the obvious way
 *    to get this wrong.
 *  - Only an INCREASE fires. `character:updated` pushes on colour changes and
 *    attribute allocation too, where the level is unchanged; and a level that
 *    somehow decreased is not a reward.
 *  - A jump of more than one level fires ONCE, not once per level. Nothing in
 *    the game grants two levels at a time today, but a reconnect that
 *    reconciles a stale sheet would look exactly like it.
 *
 * ─── Why it can be silent and that is fine ──────────────────────────────────
 * If the mixer's context has not been unlocked yet, the sound is skipped
 * rather than queued. A stinger that fires late — attached to the gesture
 * that happened to unlock audio, long after the level-up it was celebrating —
 * is worse than one that never plays.
 */
import { gameStateStore } from '../state/game-state';
import type { MixerHandle } from './mixer';
import { TRACKS } from './tracks';

/**
 * Starts watching. Returns an unsubscribe fn.
 *
 * The clip is pre-loaded immediately so the first level-up does not wait on a
 * network round trip — decoding it costs one small fetch and the reward
 * landing a second late is the thing this exists to avoid.
 */
export function createLevelUpSound(mixer: MixerHandle): () => void {
  void mixer.load(TRACKS.levelUp.path).catch(() => {
    // Pre-warm only. A failure here is retried by the play path, and if that
    // fails too the game is simply quiet — see the module header.
  });

  // `null` means "no sheet seen yet", which is distinct from any real level.
  let lastLevel: number | null = null;

  return gameStateStore.subscribe((s) => {
    // `CharacterSheet` is a union — an UNCREATED sheet has no level at all,
    // and must not reset the baseline either, or creating a character after
    // browsing the select screen would read as a level-up.
    const sheet = s.character;
    if (!sheet || sheet.state !== 'ACTIVE') return;
    const level = sheet.level;
    if (typeof level !== 'number') return;

    const previous = lastLevel;
    lastLevel = level;

    // First sheet of the session — establish the baseline, celebrate nothing.
    if (previous === null) return;
    if (level <= previous) return;

    // Unlocked contexts only. See the header.
    if (!mixer.isRunning()) return;

    void mixer.playOnce(TRACKS.levelUp.path, TRACKS.levelUp.channel).catch(() => {
      // Losing a stinger must not throw inside a store listener — every other
      // subscriber to `gameStateStore` would stop receiving updates.
    });
  });
}
